import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { PostHogProvider } from 'posthog-js/react'
import './i18n/i18n'
import './lib/zod-i18n'
import '@fontsource-variable/inter'
import './index.css'

// Import the generated route tree
import { routeTree } from './routeTree.gen'
import { PageCrashed } from './components/PageCrashed'
import { reportCrash } from './lib/reportCrash'
import { installStaleChunkReload } from './lib/staleChunk'
import { recordClick, recordSubmit, trackRouterNavigation } from './lib/uiTrace'

// Create a new router instance
// A render crash stays inside the route that threw: defaultErrorComponent wraps
// each route's component, so the shell (sidebar, header) keeps rendering.
const router = createRouter({
  routeTree,
  defaultErrorComponent: ({ error }) => <PageCrashed error={error} />,
  defaultOnCatch: reportCrash,
})

// A tab opened before a deploy reloads instead of crashing on a chunk the new
// image no longer serves (#1719, lib/staleChunk.ts).
installStaleChunkReload()

// Install the UI breadcrumb trace (spec 2026-09-06-17): one capture-phase
// listener each for clicks and submits, plus a route-resolution subscriber.
// No React state involved — see lib/uiTrace.ts for the ring buffers, the
// privacy rules, and why the initial navigation is recorded via the
// router's own first `onResolved` rather than eagerly here.
document.addEventListener('click', recordClick, { capture: true })
document.addEventListener('submit', recordSubmit, { capture: true })
trackRouterNavigation(router)

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
      options={{
        api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
        defaults: '2025-05-24',
        capture_exceptions: true, // This enables capturing exceptions using Error Tracking, set to false if you don't want this
        debug: false,
      }}
    >
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </PostHogProvider>
  </StrictMode>,
)
